// Uploaded graphic plates: the channel's own finished designs (a 16:9 picture
// with an empty text panel). Upload them, then mark where the words go and
// pick how the plate moves — the server animates it into a loop.
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FiX, FiUpload, FiLoader, FiCheck, FiSliders, FiCopy, FiSend, FiAlertTriangle, FiRotateCcw, FiPaperclip, FiUsers } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { showError } from '../FullStory/shared';
import { useStillPreview, StillPreview, TextToggle, BlurControls, blurBoxes } from './PlatePackage';
import { useNow, since, etaText } from './plateTime';
import { PlateLive } from './PlateLive';
import { PeopleEditor } from './PeopleEditor';
import { FontLibrary } from './PlateFonts';

// The words a plate's preview shows in its panel (as the server's sample).
const SAMPLE_WORDS = 'ఇక్కడ మీ వార్తలోని ముఖ్యమైన వాక్యం కనిపిస్తుంది';

export const MOTIONS = ['push', 'drift', 'still'];
// Effects over a plate (up to MAX_FX), and how it comes in and goes out (as the server's PlateUpload).
export const FX = ['thunder', 'earthquake', 'rain', 'snow', 'embers', 'smoke', 'glitch', 'flare', 'confetti', 'sparkle', 'pulse', 'searchlight', 'grain', 'bokeh', 'dust'];
export const MAX_FX = 3;
export const TRANSITIONS = ['build', 'whip', 'slide', 'slideright', 'slideup', 'wipe', 'flash', 'blur', 'shutter', 'split', 'glitch', 'fade', 'none'];
// People on layers of their own: how they move (as the server's PlatePeople).
export const PEOPLE_MOTIONS = ['breathe', 'drift', 'parallax', 'still'];
export const PEOPLE_ENTRANCES = ['slide', 'rise', 'zoom', 'fade', 'none'];
export const PEOPLE_STRENGTHS = ['subtle', 'medium', 'strong'];

/**
 * A plate's people (and objects): found once, in the background (about a
 * minute; a hint says who) — then who moves is picked, others added, outlines
 * mended in the editor; how they move is tried live in the Animation tab.
 */
export const PeopleSettings = ({ plate, people, look, onLook, onFound, onTry }) => {
  const t = useT();
  const [state, setState] = useState(people || null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState('');
  const [hint, setHint] = useState('');
  const [again, setAgain] = useState(false);
  const [editing, setEditing] = useState(false);
  const working = state?.status === 'working';
  const now = useNow(working);
  useEffect(() => { setState(people || null); }, [people]);
  // While they are being found: ask now and then (the gallery does too).
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(async () => {
      try {
        const data = await api.plateTemplates();
        const next = data.templates.find((x) => x.id === plate.id)?.people || null;
        setState(next);
        if (next?.status === 'ready') { setAgain(false); onFound?.(next); }
      } catch { /* the next try */ }
    }, 3000);
    return () => clearInterval(timer);
  }, [working, plate.id, onFound]);
  const start = async () => {
    setStarting(true);
    setFailed('');
    try {
      setState((await api.startPlatePeople(plate.id, hint.trim())).people);
    } catch (err) {
      // Said here, by the button (not only in a toast that goes away).
      setFailed(err?.response?.data?.message || err.message || 'error');
      showError(err);
    } finally {
      setStarting(false);
    }
  };
  const ready = state?.status === 'ready' && state.current;
  const chips = (values, key, label) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-gray-500 w-20">{label}</span>
      {values.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => { onLook({ [key]: v }); onTry(); }}
          aria-pressed={look[key] === v}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border ${look[key] === v ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
        >
          {t(`plateTemplates.people.${key}.${v}`)}
        </button>
      ))}
    </div>
  );
  const finder = (
    <div className="space-y-1.5">
      <input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={300} placeholder={t('plateTemplates.people.hintPlaceholder')} aria-label={t('plateTemplates.people.hintPlaceholder')} className="input text-xs py-1.5" />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={start} disabled={starting} className="btn btn-outline btn-sm flex items-center gap-1.5">
          {starting ? <FiLoader size={13} className="animate-spin" /> : <FiCheck size={13} />} {t(state?.status === 'ready' ? 'plateTemplates.people.again' : 'plateTemplates.people.find')}
        </button>
        <span className="text-[11px] text-gray-500">{t('plateTemplates.people.cost')}</span>
        {ready && <button type="button" onClick={() => setAgain(false)} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.cancel')}</button>}
      </div>
    </div>
  );
  return (
    <div className="space-y-2 rounded-xl border border-gray-200 p-3" role="group" aria-label={t('plateTemplates.people.title')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-gray-700 font-medium">{t('plateTemplates.people.title')}</p>
          <p className="text-[11px] text-gray-500">{t('plateTemplates.people.hint')}</p>
        </div>
        {ready && (
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
            <input type="checkbox" checked={look.enabled !== false} onChange={(e) => { onLook({ enabled: e.target.checked }); onTry(); }} className="rounded border-gray-300" />
            {t('plateTemplates.people.on', { count: state.count })}
          </label>
        )}
      </div>
      {working && (
        <p className="text-xs text-gray-600 inline-flex items-center gap-1.5">
          <FiLoader size={12} className="animate-spin" /> {t(state.stage === 'filling' ? 'plateTemplates.people.filling' : 'plateTemplates.people.working')}
          <span className="tabular-nums text-gray-500">{etaText(t, state.startedAt, now, 'people')}</span>
        </p>
      )}
      {!working && (!ready || again) && (
        <>
          {finder}
          {state?.status === 'failed' && <span className="text-xs text-red-600 inline-flex items-center gap-1"><FiAlertTriangle size={12} /> {state.error}</span>}
          {failed && <span className="w-full text-xs text-red-600 inline-flex items-center gap-1"><FiAlertTriangle size={12} /> {t('plateTemplates.people.startFailed', { error: failed })}</span>}
          {state?.status === 'ready' && !state.current && <span className="text-xs text-amber-700">{t('plateTemplates.people.stale')}</span>}
        </>
      )}
      {ready && !again && (
        <div className="flex flex-wrap items-center gap-2">
          {state.editable ? (
            <>
              <button type="button" onClick={() => setEditing(true)} className="btn btn-primary btn-sm flex items-center gap-1.5"><FiUsers size={13} /> {t('plateTemplates.people.choose')}</button>
              <span className="text-[11px] text-gray-600">{t('plateTemplates.people.moving', { count: state.count, found: state.subjects })}</span>
            </>
          ) : <span className="text-[11px] text-amber-700">{t('plateTemplates.people.oldWay')}</span>}
          <button type="button" onClick={() => setAgain(true)} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.people.again')}</button>
          {state.uncovered && <span className="w-full text-[11px] text-amber-700 inline-flex items-center gap-1"><FiAlertTriangle size={12} /> {t('plateTemplates.people.uncoveredShort')}</span>}
          {state.task?.status === 'working' && <span className="w-full text-[11px] text-gray-600 inline-flex items-center gap-1"><FiLoader size={11} className="animate-spin" /> {t(`plateTemplates.people.editor.task.${state.task.kind}`)}</span>}
        </div>
      )}
      {ready && look.enabled !== false && (
        <div className="space-y-1.5">
          {chips(PEOPLE_MOTIONS, 'motion', t('plateTemplates.people.motionLabel'))}
          {chips(PEOPLE_ENTRANCES, 'entrance', t('plateTemplates.people.entranceLabel'))}
          {chips(PEOPLE_STRENGTHS, 'strength', t('plateTemplates.people.strengthLabel'))}
        </div>
      )}
      {editing && (
        <PeopleEditor
          plate={plate}
          look={look}
          onClose={() => { setEditing(false); onFound?.(state); onTry(); }}
          onChanged={() => onFound?.(state)}
        />
      )}
    </div>
  );
};
const EFFECTS = ['flat', '3d'];
export const INKS = ['auto', 'dark', 'light'];
const MIN_W = 0.05;
const MIN_H = 0.04;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * The words' font (from every font: search, families, weights, upload) and
 * style (flat or 3D). onChange({ font } | { effect }); `busy` greys it out while a change saves.
 */
export const FontPicker = ({ font = 'pallavi', effect = 'flat', onChange, busy = false }) => {
  const t = useT();
  return (
    <div className={`space-y-2 ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-gray-700 font-medium mr-1">{t('plateTemplates.font')}</p>
        <div role="radiogroup" aria-label={t('plateTemplates.textStyle')} className="inline-flex p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
          {EFFECTS.map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={effect === v}
              onClick={() => effect !== v && onChange({ effect: v })}
              className={`px-2.5 py-1 rounded-md ${effect === v ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t(`plateTemplates.effect.${v}`)}
            </button>
          ))}
        </div>
      </div>
      <FontLibrary value={font} onChange={(id) => font !== id && onChange({ font: id })} />
    </div>
  );
};

/**
 * The prompt that would make a plate like this one — to copy into an image
 * generator, with what each part does and tips, so editors learn to write them.
 */
export const PromptPanel = ({ templateId }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [prompt, setPrompt] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPrompt(null);
    api.plateTemplatePrompt(templateId).then((p) => { if (!cancelled) setPrompt(p); }).catch(showError);
    return () => { cancelled = true; };
  }, [templateId]);

  const copy = async (what, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch (e) {
      showError(e);
    }
  };

  if (!prompt) {
    return <p className="px-5 pt-4 text-sm text-gray-500 inline-flex items-center gap-2"><FiLoader size={14} className="animate-spin" /> {t('plateTemplates.loading')}</p>;
  }
  const copyButton = (what, text, label) => (
    <button type="button" onClick={() => copy(what, text)} className="btn btn-ghost btn-sm flex items-center gap-1 text-xs">
      {copied === what ? <FiCheck size={13} className="text-green-600" /> : <FiCopy size={13} />} {copied === what ? t('plateTemplates.copied') : label}
    </button>
  );

  return (
    <section className="px-5 pt-4 space-y-4" aria-label={t('plateTemplates.promptTitle')}>
      <div className="rounded-xl border border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200">
          <div>
            <p className="text-sm font-semibold text-gray-900">{t('plateTemplates.promptTitle')}</p>
            <p className="text-xs text-gray-500">{t('plateTemplates.promptHint')}</p>
          </div>
          {copyButton('prompt', prompt.text, t('plateTemplates.copyPrompt'))}
        </div>
        <p className="px-3 py-3 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap select-text">{prompt.text}</p>
        {prompt.avoid && (
          <div className="flex items-start justify-between gap-2 px-3 py-2 border-t border-gray-200">
            <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">{t('plateTemplates.promptAvoid')}</span> {prompt.avoid}</p>
            {copyButton('avoid', prompt.avoid, t('plateTemplates.copy'))}
          </div>
        )}
      </div>
      {prompt.parts.length > 0 && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-1.5">{t('plateTemplates.promptParts')}</p>
          <dl className="space-y-2">
            {prompt.parts.map((part) => (
              <div key={part.label}>
                <dt className="text-xs font-semibold text-primary-700">{lang === 'te' ? part.label_te : part.label}</dt>
                <dd className="text-xs text-gray-600 leading-relaxed">{lang === 'te' ? part.detail_te : part.detail}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-1.5">{t('plateTemplates.promptTips')}</p>
          <ul className="list-disc pl-4 space-y-1">
            {prompt.tips.map((tip) => <li key={tip} className="text-xs text-gray-600 leading-relaxed">{tip}</li>)}
          </ul>
        </div>
      </div>
      )}
    </section>
  );
};

const MAX_CHANGE = 2000;
const MAX_REFERENCES = 6;
const REFERENCE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * A created plate's thread: write what to change and the design is edited
 * into a new version (the rest kept as it is). Every version stays — any one
 * can be shown again. onChanged() reloads the gallery (which keeps asking
 * while a change is being made).
 */
export const PlateThread = ({ plate, onChanged }) => {
  const t = useT();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // Reference pictures for the next change: [{ file, url }] (url: a preview).
  const [images, setImages] = useState([]);
  // Previews are released when a picture is removed or sent, and when the thread closes.
  const shown = useRef([]);
  shown.current = images;
  useEffect(() => () => shown.current.forEach((i) => URL.revokeObjectURL(i.url)), []);
  const drop = (k) => setImages((now) => {
    URL.revokeObjectURL(now[k].url);
    return now.filter((_, j) => j !== k);
  });
  const edits = plate.edits || [];
  const versions = plate.versions?.length ? plate.versions : [{ n: 1 }];
  const working = edits.some((e) => e.status === 'working');
  const now = useNow(working);
  const thumb = (n) => api.plateTemplateUrl(plate.id, `v${n}.jpg`, plate.updatedAt);
  const when = (at) => (at ? new Date(at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

  const attach = (fileList) => {
    const picked = Array.from(fileList || []).filter((f) => REFERENCE_TYPES.includes(f.type));
    if (picked.length < (fileList?.length || 0)) showError(new Error(t('plateTemplates.referenceType')));
    const room = MAX_REFERENCES - images.length;
    if (picked.length > room) showError(new Error(t('plateTemplates.referenceMax', { count: MAX_REFERENCES })));
    setImages((now) => [...now, ...picked.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || working || busy) return;
    setBusy(true);
    try {
      await api.editCreatedPlate(plate.id, text.trim(), images.map((i) => i.file));
      setText('');
      images.forEach((i) => URL.revokeObjectURL(i.url));
      setImages([]);
      await onChanged();
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const restore = async (n) => {
    if (n === plate.version || working || busy) return;
    setBusy(true);
    try {
      await api.restorePlateVersion(plate.id, n);
      await onChanged();
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="px-5 pt-4 space-y-3" aria-label={t('plateTemplates.threadTitle')}>
      <div>
        <p className="text-sm font-semibold text-gray-900">{t('plateTemplates.threadTitle')}</p>
        <p className="text-xs text-gray-500">{t('plateTemplates.threadHint')}</p>
      </div>

      {/* Versions: the current one marked; any other can be shown again. */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {versions.map((v) => {
          const current = v.n === (plate.version || 1);
          return (
            <button
              key={v.n}
              type="button"
              onClick={() => restore(v.n)}
              disabled={current || working || busy}
              title={current ? t('plateTemplates.versionCurrent') : t('plateTemplates.versionUse')}
              className={`flex-shrink-0 w-32 rounded-lg border text-left overflow-hidden ${current ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-400'} disabled:cursor-default`}
            >
              <img src={thumb(v.n)} alt="" loading="lazy" className="w-full aspect-video object-cover bg-gray-100" />
              <span className="flex items-center justify-between gap-1 px-1.5 py-1 text-[11px]">
                <span className="font-medium text-gray-700">{v.n === 1 ? t('plateTemplates.versionOriginal') : t('plateTemplates.version', { n: v.n })}</span>
                {current ? <FiCheck size={12} className="text-primary-600" /> : <FiRotateCcw size={11} className="text-gray-400" />}
              </span>
            </button>
          );
        })}
      </div>

      {edits.length > 0 && (
        <ol className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {edits.map((e) => (
            <li key={e.id} className="space-y-1">
              <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-primary-50 border border-primary-100 px-3 py-2">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{e.instruction}</p>
                {e.references > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Array.from({ length: e.references }, (_, k) => (
                      <a key={k} href={api.plateTemplateUrl(plate.id, `ref-${e.id}-${k + 1}.jpg`)} target="_blank" rel="noreferrer">
                        <img src={api.plateTemplateUrl(plate.id, `ref-${e.id}-${k + 1}.jpg`)} alt="" loading="lazy" className="h-12 w-12 rounded object-cover border border-primary-100 bg-white" />
                      </a>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-gray-500 mt-0.5 text-right">{[e.byName, when(e.at)].filter(Boolean).join(' · ')}</p>
              </div>
              <div className="max-w-[85%] text-xs">
                {e.status === 'working' && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 text-gray-600 px-2.5 py-1">
                    <FiLoader size={12} className="animate-spin" /> {t('plateTemplates.changeWorking')} <span className="tabular-nums text-gray-500">{etaText(t, e.at, now, 'change')}</span>
                  </span>
                )}
                {e.status === 'failed' && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 px-2.5 py-1">
                    <FiAlertTriangle size={12} /> {e.error || t('plateTemplates.changeFailed')}
                  </span>
                )}
                {e.status === 'done' && (
                  <span className="inline-flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 p-1 pr-2.5">
                    <img src={thumb(e.version)} alt="" className="h-9 aspect-video rounded object-cover" loading="lazy" />
                    <span className="text-gray-700">{t('plateTemplates.changeDone', { n: e.version })}</span>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {images.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t('plateTemplates.references')}>
          {images.map((img, k) => (
            <li key={img.url} className="relative">
              <img src={img.url} alt={img.file.name} className="h-14 w-14 rounded-lg object-cover border border-gray-200" />
              <button
                type="button"
                onClick={() => drop(k)}
                aria-label={t('plateTemplates.referenceRemove')}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-black"
              >
                <FiX size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={send} className="flex items-end gap-2">
        <label
          className={`btn btn-ghost flex-shrink-0 px-2.5 ${working || images.length >= MAX_REFERENCES ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
          title={t('plateTemplates.referenceAttach', { count: MAX_REFERENCES })}
        >
          <FiPaperclip size={16} />
          <span className="sr-only">{t('plateTemplates.referenceAttach', { count: MAX_REFERENCES })}</span>
          <input
            type="file"
            accept={REFERENCE_TYPES.join(',')}
            multiple
            className="sr-only"
            disabled={working || images.length >= MAX_REFERENCES}
            onChange={(e) => { attach(e.target.files); e.target.value = ''; }}
          />
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(e); }}
          placeholder={t('plateTemplates.changePlaceholder')}
          maxLength={MAX_CHANGE}
          rows={2}
          disabled={working}
          className="input flex-1 text-sm resize-y"
        />
        <button type="submit" disabled={!text.trim() || working || busy} className="btn btn-primary flex items-center gap-1.5 flex-shrink-0">
          {busy ? <FiLoader size={14} className="animate-spin" /> : <FiSend size={14} />} {t('plateTemplates.changeSend')}
        </button>
      </form>
      <p className="text-[11px] text-gray-500">{t('plateTemplates.changeCost')}</p>
    </section>
  );
};

/** Choose one or more plate pictures and a category; each is uploaded in turn. */
export const UploadDialog = ({ categories, onClose, onUploaded }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('general');
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    const made = [];
    const failed = [];
    for (const [i, file] of files.entries()) {
      setProgress({ done: i + 1, total: files.length });
      try {
        // eslint-disable-next-line no-await-in-loop
        made.push(await api.uploadPlateTemplate(file, { category }));
      } catch (err) {
        failed.push(t('plateTemplates.uploadFailed', { name: file.name, error: err?.response?.data?.message || err.message }));
      }
    }
    setProgress(null);
    setErrors(failed);
    if (made.length) onUploaded(made, failed.length > 0);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={progress ? undefined : onClose} role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-xl max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain bg-white rounded-2xl shadow-2xl p-5 pb-0 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiUpload size={18} className="text-primary-600" /> {t('plateTemplates.uploadTitle')}</h2>
          <button type="button" onClick={onClose} disabled={Boolean(progress)} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm"><FiX size={18} /></button>
        </div>
        <p className="text-sm text-gray-600">{t('plateTemplates.uploadHint')}</p>
        <label className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 hover:border-primary-400 bg-gray-50 px-4 py-6 cursor-pointer text-center">
          <FiUpload size={22} className="text-gray-400" />
          <span className="text-sm font-medium text-primary-700">{files.length ? t('plateTemplates.uploadChosen', { count: files.length }) : t('plateTemplates.uploadChoose')}</span>
          <span className="text-xs text-gray-500">{t('plateTemplates.uploadRules')}</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="sr-only"
            onChange={(e) => { setFiles(Array.from(e.target.files || [])); setErrors([]); }}
          />
        </label>
        {files.length > 0 && (
          <ul className="max-h-28 overflow-y-auto text-xs text-gray-600 space-y-0.5">
            {files.map((f) => <li key={`${f.name}-${f.size}`} className="truncate">{f.name}</li>)}
          </ul>
        )}
        <label className="block text-sm">
          <span className="text-gray-700 font-medium">{t('plateTemplates.uploadCategory')}</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="input mt-1 text-sm">
            {categories.map((c) => <option key={c.id} value={c.id}>{lang === 'te' ? c.te : c.name}</option>)}
          </select>
        </label>
        {errors.length > 0 && (
          <ul className="rounded-lg bg-red-50 border border-red-100 p-2 text-xs text-red-700 space-y-0.5">
            {errors.map((m) => <li key={m}>{m}</li>)}
          </ul>
        )}
        <div className="sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={Boolean(progress)} className="btn btn-secondary">{t('plateTemplates.cancel')}</button>
          <button type="submit" disabled={Boolean(progress) || files.length === 0} className="btn btn-primary flex items-center gap-2">
            {progress ? <FiLoader size={14} className="animate-spin" /> : <FiUpload size={14} />}
            {progress ? t('plateTemplates.uploading', progress) : t('plateTemplates.uploadGo')}
          </button>
        </div>
      </form>
    </div>
  );
};

// Each text area's colour in the box editor (a plate has one; a package up to two).
const BOX_TONES = [
  { border: 'border-primary-400', fill: 'bg-primary-400/15', tag: 'bg-primary-600', handle: 'border-primary-600' },
  { border: 'border-amber-400', fill: 'bg-amber-400/15', tag: 'bg-amber-600', handle: 'border-amber-600' },
];
// Blur areas: dashed, in sky blue (tone: 'blur').
const BLUR_TONE = { border: 'border-sky-300 border-dashed', fill: 'bg-sky-300/20 backdrop-blur-sm', tag: 'bg-sky-600', handle: 'border-sky-600' };
// A square or circle on the 16:9 picture: its height (a fraction of the height) for a width (a fraction of the width).
const squareH = (w) => (w * 16) / 9;

/**
 * Text areas on the plate: drag a box to move it, a corner to resize.
 * boxes: [{ box: { x, y, w, h } (fractions of the picture), label,
 *   tone ('blur' for a blur area), shape ('ellipse' | 'rounded' | 'rect'),
 *   lock (keeps a square or a circle round as it is resized) }];
 * onChange(index, box).
 */
export const BoxesEditor = ({ src, boxes, onChange }) => {
  const frame = useRef(null);
  const drag = useRef(null);

  const start = (index, mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = frame.current.getBoundingClientRect();
    drag.current = { index, mode, rect, from: { x: e.clientX, y: e.clientY }, box: boxes[index].box, lock: Boolean(boxes[index].lock) };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const move = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.from.x) / d.rect.width;
    const dy = (e.clientY - d.from.y) / d.rect.height;
    const b = d.box;
    let next;
    if (d.mode === 'move') {
      next = { ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) };
    } else {
      let x0 = b.x; let y0 = b.y; let x1 = b.x + b.w; let y1 = b.y + b.h;
      if (d.mode.includes('l')) x0 = clamp(b.x + dx, 0, x1 - MIN_W);
      if (d.mode.includes('r')) x1 = clamp(b.x + b.w + dx, x0 + MIN_W, 1);
      if (d.mode.includes('t')) y0 = clamp(b.y + dy, 0, y1 - MIN_H);
      if (d.mode.includes('b')) y1 = clamp(b.y + b.h + dy, y0 + MIN_H, 1);
      next = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
      if (d.lock) {
        // Square / circle: the height follows the width, from the corner held still.
        let w = next.w; let h = squareH(w);
        const room = d.mode.includes('t') ? b.y + b.h : 1 - b.y;
        if (h > room) { h = room; w = (h * 9) / 16; }
        const x = d.mode.includes('l') ? b.x + b.w - w : b.x;
        next = { x, y: d.mode.includes('t') ? b.y + b.h - h : b.y, w, h };
      }
    }
    onChange(d.index, next);
  }, [onChange]);

  const end = () => { drag.current = null; };
  const pct = (v) => `${(v * 100).toFixed(2)}%`;
  const corner = 'absolute w-4 h-4 rounded-full bg-white border-2 shadow -translate-x-1/2 -translate-y-1/2 touch-none';

  return (
    <div ref={frame} className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden select-none touch-none" onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
      <img src={src} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <div className="absolute inset-0 bg-black/25 pointer-events-none" />
      {boxes.map(({ box, label, tone: toneName, shape }, i) => {
        const tone = toneName === 'blur' ? BLUR_TONE : BOX_TONES[i % BOX_TONES.length];
        const round = shape === 'ellipse' ? 'rounded-[50%]' : shape === 'rounded' ? 'rounded-xl' : '';
        return (
          <div
            key={i}
            role="slider"
            aria-label={label}
            aria-valuetext={`${pct(box.x)} ${pct(box.y)} ${pct(box.w)} × ${pct(box.h)}`}
            tabIndex={0}
            onPointerDown={start(i, 'move')}
            className={`absolute border-2 ${tone.border} ${tone.fill} ${round} cursor-move ${boxes.length === 1 ? 'shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]' : ''}`}
            style={{ left: pct(box.x), top: pct(box.y), width: pct(box.w), height: pct(box.h) }}
          >
            <span className={`absolute ${box.y < 0.06 ? 'top-0.5' : '-top-6'} left-0 rounded ${tone.tag} text-white text-[11px] px-1.5 py-0.5 whitespace-nowrap`}>{label}</span>
            {[['tl', 0, 0, 'cursor-nwse-resize'], ['tr', 100, 0, 'cursor-nesw-resize'], ['bl', 0, 100, 'cursor-nesw-resize'], ['br', 100, 100, 'cursor-nwse-resize']].map(([mode, left, top, cursor]) => (
              <span key={mode} onPointerDown={start(i, mode)} className={`${corner} ${tone.handle} ${cursor}`} style={{ left: `${left}%`, top: `${top}%` }} />
            ))}
          </div>
        );
      })}
    </div>
  );
};


/** An uploaded plate's setup: name, category, text area, text colour, animation. */
export const PlateSetup = ({ plate, categories, onClose, onSaved }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [name, setName] = useState(plate.name || '');
  const [category, setCategory] = useState(plate.category || 'general');
  const [box, setBox] = useState(plate.box);
  const [ink, setInk] = useState(plate.ink || 'auto');
  const [motion, setMotion] = useState(plate.motion || 'push');
  const [dust, setDust] = useState(Boolean(plate.dust));
  const [fx, setFx] = useState(plate.fx || []);
  const [transition, setTransition] = useState(plate.transition || 'build');
  // How its people move (when found): saved with the plate, tried live first.
  const [peopleLook, setPeopleLook] = useState({
    motion: plate.people?.motion || 'breathe', entrance: plate.people?.entrance || 'slide', strength: plate.people?.strength || 'medium', enabled: plate.people?.enabled !== false,
  });
  const [peopleFound, setPeopleFound] = useState(Boolean(plate.people?.status === 'ready' && plate.people?.current));
  const toggleFx = (id) => setFx((now) => (now.includes(id) ? now.filter((x) => x !== id) : now.length >= MAX_FX ? now : [...now, id]));
  const [look, setLook] = useState({ font: plate.font || 'pallavi', effect: plate.effect || 'flat' });
  const [blurs, setBlurs] = useState(plate.blurs || []);
  const [view, setView] = useState('areas');
  const [showText, setShowText] = useState(true);
  const [busy, setBusy] = useState(false);
  // Every change shows at once: the plate as it will look, drawn by the server in a moment.
  const preview = useStillPreview(plate.id, {
    areas: [{ box, role: 'common', text: SAMPLE_WORDS, ink, font: look.font, effect: look.effect }], blurs: [], plateBlurs: blurs, noText: !showText,
  }, view === 'preview');

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved(await api.updatePlateTemplate(plate.id, { name, category, box, ink, motion, dust, fx, transition, blurs, ...look, ...(peopleFound ? { people: peopleLook } : {}) }));
    } catch (err) {
      showError(err);
      setBusy(false);
    }
  };

  const autoInk = t(`plateTemplates.ink.${plate.autoInk === 'dark' ? 'dark' : 'light'}`);
  const choice = (value, current, set, text) => (
    <button
      key={value}
      type="button"
      onClick={() => set(value)}
      aria-pressed={current === value}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${current === value ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
    >
      {text}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4" onClick={busy ? undefined : onClose} role="dialog" aria-modal="true">
      <form onSubmit={save} className="w-full max-w-4xl max-h-[94vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-4 sm:p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiSliders size={18} className="text-primary-600" /> {t('plateTemplates.editTitle')}</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm"><FiX size={18} /></button>
        </div>
        <p className="text-sm text-gray-600">{t('plateTemplates.editHint')}</p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div role="tablist" className="inline-flex p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
            {['areas', 'preview', 'animation'].map((v) => (
              <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)} className={`px-3 py-1 rounded-md ${view === v ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {t(`plateTemplates.setupView.${v}`)}
              </button>
            ))}
          </div>
          {view === 'preview' && <TextToggle on={showText} onChange={setShowText} />}
        </div>
        {view === 'areas' ? (
          <BoxesEditor
            src={api.plateTemplateUrl(plate.id, 'source.jpg')}
            boxes={[{ box, label: t('plateTemplates.textArea') }, ...blurBoxes(blurs, t)]}
            onChange={(i, next) => (i === 0 ? setBox(next) : setBlurs((now) => now.map((b, k) => (k === i - 1 ? { ...b, ...next } : b))))}
          />
        ) : view === 'preview' ? (
          <StillPreview preview={preview} label={t('plateTemplates.setupView.preview')} />
        ) : (
          // The plate playing live as chosen (motion, effects, transition — before saving); its text area as saved.
          <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900">
            <PlateLive plateId={plate.id} words look={{ motion, dust, fx, transition, ...(peopleFound ? { people: peopleLook } : {}) }} version={`${plate.updatedAt}|${peopleFound}|${plate.people?.rev || 0}`} poster={api.plateTemplateUrl(plate.id, 'poster.jpg', plate.updatedAt)} />
          </div>
        )}
        <BlurControls blurs={blurs} onChange={setBlurs} onAdded={() => setView('areas')} />
        {blurs.length > 0 && <p className="text-[11px] text-gray-500 -mt-2">{t('plateTemplates.blur.plateHint')}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-gray-700 font-medium">{t('plateTemplates.editName')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="input mt-1 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700 font-medium">{t('plateTemplates.uploadCategory')}</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="input mt-1 text-sm">
              {categories.map((c) => <option key={c.id} value={c.id}>{lang === 'te' ? c.te : c.name}</option>)}
            </select>
          </label>
        </div>
        <div className="space-y-1.5">
          <p className="text-sm text-gray-700 font-medium">{t('plateTemplates.editInk')}</p>
          <div className="flex flex-wrap gap-1.5">
            {INKS.map((v) => choice(v, ink, setInk, v === 'auto' ? t('plateTemplates.ink.auto', { ink: autoInk }) : t(`plateTemplates.ink.${v}`)))}
          </div>
        </div>
        <FontPicker font={look.font} effect={look.effect} onChange={(change) => setLook((l) => ({ ...l, ...change }))} />
        <div className="space-y-1.5">
          <p className="text-sm text-gray-700 font-medium">{t('plateTemplates.editMotion')}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {MOTIONS.map((v) => choice(v, motion, setMotion, t(`plateTemplates.motion.${v}`)))}
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 ml-2">
              <input type="checkbox" checked={dust} onChange={(e) => setDust(e.target.checked)} className="rounded border-gray-300" />
              {t('plateTemplates.editDust')}
            </label>
          </div>
        </div>
        <PeopleSettings
          plate={plate}
          people={plate.people}
          look={peopleLook}
          onLook={(change) => setPeopleLook((l) => ({ ...l, ...change }))}
          onFound={() => { setPeopleFound(true); setView('animation'); }}
          onTry={() => setView('animation')}
        />
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-gray-700 font-medium">{t('plateTemplates.fx.title')}</p>
            <span className="text-[11px] text-gray-500">{t('plateTemplates.fx.hint', { count: fx.length, max: MAX_FX })}</span>
          </div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('plateTemplates.fx.title')}>
            {FX.map((id) => {
              const on = fx.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { toggleFx(id); setView('animation'); }}
                  aria-pressed={on}
                  disabled={!on && fx.length >= MAX_FX}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border disabled:opacity-40 ${on ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                >
                  {t(`plateTemplates.fx.${id}`)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-sm text-gray-700 font-medium">{t('plateTemplates.transition.title')}</p>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('plateTemplates.transition.title')}>
            {TRANSITIONS.map((v) => choice(v, transition, (x) => { setTransition(x); setView('animation'); }, t(`plateTemplates.transition.${v}`)))}
          </div>
          <p className="text-[11px] text-gray-500">{t('plateTemplates.transition.hint')}</p>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="btn btn-secondary">{t('plateTemplates.cancel')}</button>
          <button type="submit" disabled={busy || !name.trim()} className="btn btn-primary flex items-center gap-2">
            {busy ? <FiLoader size={14} className="animate-spin" /> : <FiCheck size={14} />}
            {busy ? t('plateTemplates.saving') : t('plateTemplates.save')}
          </button>
        </div>
      </form>
    </div>
  );
};
